import { FC, Fragment, MouseEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, matchPath, Link as RouterLink, NavLink as RouterNavLink } from "react-router-dom";
import { closeSnackbar } from "notistack";

import { Menu, MenuItem } from "@mui/material";

import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import { ResetUserSettingsDialog } from "../../reset-user-settings-dialog";

import {
  StyledLoginButton,
  StyledProfileButton,
  StyledProfileLogin,
  StyledProfileRole,
  StyledUnfoldMore,
  StyledWrapper,
} from "./styled";

export const Sections: FC = () => {
  const location = useLocation();
  const { formatMessage } = useIntl();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [isResetSettingsOpen, setIsResetSettingsOpen] = useState(false);

  const user = useUser<IUser>();

  const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
    setAnchor(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchor(null);
  };

  const logout = async (e: MouseEvent): Promise<void> => {
    e.preventDefault();
    handleMenuClose();
    closeSnackbar();
    await user.logOut();
  };

  const matchesUrl =
    !!matchPath({ path: "/login", end: true }, location.pathname) ||
    !!matchPath({ path: "/set-new-password", end: true }, location.pathname) ||
    location.pathname === "/";

  const isLoginPage = !!matchPath({ path: "/login", end: true }, location.pathname);

  if (isLoginPage) {
    return null;
  }

  if (!user.isAuthenticated()) {
    if (matchesUrl) {
      return null;
    }

    return (
      <StyledLoginButton variant="outlined" size="large" component={RouterLink} to="/login">
        <FormattedMessage id="components.header.menu.login" />
      </StyledLoginButton>
    );
  }

  return (
    <Fragment>
      <StyledProfileButton
        aria-owns={anchor ? "material-appbar" : undefined}
        aria-haspopup="true"
        onClick={handleMenuOpen}
        endIcon={<StyledUnfoldMore fill="#363846" />}
      >
        <StyledWrapper>
          <StyledProfileLogin>{user.profile.login}</StyledProfileLogin>
          <StyledProfileRole>
            {" [" + formatMessage({ id: `enums.userGroup.${user.profile.userGroup}` }) + "]"}
          </StyledProfileRole>
        </StyledWrapper>
      </StyledProfileButton>
      <Menu id="material-appbar" anchorEl={anchor} open={!!anchor} onClose={handleMenuClose}>
        <MenuItem
          color="inherit"
          component={RouterNavLink}
          to="/profile/general"
          onClick={handleMenuClose}
          selected={
            !!matchPath({ path: "/profile", end: true }, location.pathname) ||
            !!matchPath({ path: "/profile/:tab" }, location.pathname)
          }
        >
          <FormattedMessage id="components.header.menu.settings" />
        </MenuItem>
        <MenuItem
          disabled={user.isSavingSettings}
          onClick={() => {
            if (user.isSavingSettings) return;
            handleMenuClose();
            setIsResetSettingsOpen(true);
          }}
        >
          <FormattedMessage id="form.buttons.resetSettings" />
        </MenuItem>
        {user.isAdmin() && (
          <MenuItem
            color="inherit"
            component={RouterNavLink}
            to="/users"
            onClick={handleMenuClose}
            selected={!!matchPath({ path: "/users" }, location.pathname)}
          >
            <FormattedMessage id="components.header.menu.users" />
          </MenuItem>
        )}
        <MenuItem to="/logout" onClick={e => void logout(e)} component={RouterNavLink}>
          <FormattedMessage id="components.header.menu.logout" />
        </MenuItem>
      </Menu>
      <ResetUserSettingsDialog open={isResetSettingsOpen} onClose={() => setIsResetSettingsOpen(false)} />
    </Fragment>
  );
};
